import barba from '@barba/core';
import gsap from 'gsap';
import { pathToRegexp, match, parse, compile } from "path-to-regexp";
import carousel from './carousel';
import dropdownModule from './dropdown';
import search from './search';
import mobile from './mobile';
import heroCarousel from './hero-carousel';
import homeBanner from './home-banner';
import gallery from './gallery';
import { sidebarMod } from './sidebar';
import initVideoModal from './video-modal';

const pageAnimEl = document.getElementById('page-anim');
const body = document.body;

function initResizeListener() {
	// Reference: http://www.html5rocks.com/en/tutorials/speed/animations/
	let ticking = false;

	window.addEventListener('resize', function (e) {
		if (!ticking) {
			window.requestAnimationFrame(function () {
				// Reset page animation if user resizes page during animation
				resetAnim();
				ticking = false;
			});

			ticking = true;
		}
	});
}

function checkHeaderClasses(data) {
	const parser = new DOMParser();
	const resHTML = parser.parseFromString(data.next.html, "text/html");
	const newHeader = resHTML.querySelector('#header');
	const header = document.getElementById('header');
	header.classList.remove(...header.classList);
	header.classList.add(...newHeader.classList);
}

function resetAnim() {
	const activeAnim = gsap.getTweensOf(pageAnimEl)[0];
	if (!activeAnim) {
		return;
	}
	activeAnim.progress(0);
}

function leaveAnim() {
	return gsap.fromTo(pageAnimEl, {
		display: 'block',
		xPercent: -100,
		"clip-path": "polygon(0 0, 75% 0, 100% 100%, 0% 100%)",
	}, {
		xPercent: 100,
		"clip-path": "polygon(0 0, 100% 0, 100% 100%, 0% 100%)",
		duration: 0.5,
		ease: 'power2.out',
		immediateRender: false
	});
}

function afterAnim() {
	return gsap.fromTo(pageAnimEl, {
		xPercent: 100,
	}, {
		xPercent: 200,
		display: 'none',
		duration: 0.5,
		ease: 'power2.out',
		immediateRender: false
	});
}


export default function () {
	body.classList.remove('no-js');

	document.addEventListener('DOMContentLoaded', function (e) {
		body.classList.remove('page-is-loading');
	});

	initResizeListener();

	if ('scrollRestoration' in history) {
		history.scrollRestoration = 'manual';
	}

	const cacheIgnore = pathToRegexp("/news\\?filters=(.*)");

	const scrollPositions = new Map();

	function historyKey(href) {
		if (!href) {
			return '';
		}
		try {
			const url = new URL(href, window.location.origin);
			return `${url.pathname.replace(/\/$/, '')}${url.search}`;
		} catch (e) {
			return href;
		}
	}

	function clickedEntryHref(event) {
		if (!event || !event.target || !event.target.closest) {
			return '';
		}
		const link = event.target.closest('a[href]');
		return link ? historyKey(link.href) : '';
	}

	function saveScroll(data) {
		const pos = {
			x: window.scrollX,
			y: window.scrollY,
			entryHref: clickedEntryHref(data.event),
		};
		const keys = [
			historyKey(data.current && data.current.url && data.current.url.href),
			historyKey(barba.history.previous && barba.history.previous.url),
		];
		keys.forEach((key) => {
			if (key) {
				scrollPositions.set(key, pos);
			}
		});
	}

	function findSavedScroll(data) {
		const keys = [
			historyKey(data.next && data.next.url && data.next.url.href),
			historyKey(barba.history.current && barba.history.current.url),
		];
		for (let i = 0; i < keys.length; i++) {
			const key = keys[i];
			if (key && scrollPositions.has(key)) {
				return scrollPositions.get(key);
			}
		}
		return null;
	}

	function restoreScroll(saved) {
		const apply = () => {
			if (saved && saved.entryHref) {
				const links = document.querySelectorAll('.js-ajax-card a[href], a.card[href]');
				for (let i = 0; i < links.length; i++) {
					if (historyKey(links[i].href) === saved.entryHref) {
						const card = links[i].closest('.js-ajax-card') || links[i];
						card.scrollIntoView({ block: 'center' });
						return;
					}
				}
			}
			const savedX = saved && typeof saved.x === 'number' ? saved.x : 0;
			const savedY = saved && typeof saved.y === 'number' ? saved.y : 0;
			window.scrollTo(savedX, savedY);
		};

		apply();
		window.requestAnimationFrame(() => {
			window.requestAnimationFrame(apply);
		});

		const images = Array.from(document.querySelectorAll('main img'));
		const pending = images.filter((img) => !img.complete);
		if (pending.length) {
			let left = pending.length;
			const onDone = () => {
				left -= 1;
				if (left <= 0) {
					apply();
				}
			};
			pending.forEach((img) => {
				img.addEventListener('load', onDone, { once: true });
				img.addEventListener('error', onDone, { once: true });
			});
		}
		window.setTimeout(apply, 400);
	}

	barba.hooks.beforeLeave((data) => {
		saveScroll(data);
		dropdownModule.hide();
	});

	barba.hooks.before((data) => {
		if (data.current.url.path === data.next.url.path) {
			barba.force(data.next.url.href);
		};
	});

	barba.hooks.beforeEnter(({ next }) => {
		setTimeout(() => {

			if (typeof htmx !== 'undefined') {
				console.log('process htmx');
				htmx.process(next.container)
			}
		}, 250)
	})


	barba.hooks.beforeEnter((data) => {
		// Re-run any initialising scripts that depend on elements being present on the page (as opposed to click events):
		carousel();
		heroCarousel();
		homeBanner();
		gallery();
		initVideoModal();
		if (window.twttr) {
			twttr.widgets.load();
		}
	});

	barba.hooks.after((data) => {
		const { trigger } = data;
		if (trigger === 'back' || trigger === 'forward' || trigger === 'popstate') {
			restoreScroll(findSavedScroll(data));
		} else {
			// Reset scroll if navigating to new page
			window.scrollTo(0, 0);
		}
		checkHeaderClasses(data);
		sidebarMod.check();
	});

	barba.init({
		// debug: true,
		timeout: 5000,
		requestError: (trigger, action, url, response) => {
			// Barba should automatically redirect if it experiences a timeout but it doesn't seem to.
			// Also the url param seems to always be undefined.
			// We can use the trigger param instead (which seems to be url) and use barba.force to redirect to that url with a full page load.
			// console.log(trigger);
			// console.log(action);
			// console.log(url);
			// console.log(response);
			if (!url) {
				barba.force(trigger);
			}
		},
		transitions: [
			{
				name: 'opacity-transition',
				beforeLeave() {
					search.hideSearch();
					mobile.hide();
					mobile.hideSub();
					body.classList.add('page-is-changing');
				},
				leave: leaveAnim,
				beforeEnter() {
					body.classList.remove('page-is-changing');
				},
				after: afterAnim
			},
			{
				name: 'self',
				enter() {
					body.classList.remove('loading-ajax-cards');
					gsap.from('.js-ajax-card', {
						opacity: 0,
						y: 10,
						duration: 0.5,
						delay: 0.2,
						stagger: 0.1,
					});
				},
				beforeLeave() {
					search.hideSearch();
					mobile.hide();
					mobile.hideSub();
					body.classList.add('loading-ajax-cards');
				}
			}
		],
		cacheIgnore: [cacheIgnore],
	});
}
